import React, { useCallback, useMemo, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { LightMode as SunIcon, DarkModeOutlined as MoonIcon } from "@mui/icons-material";
import { useIntl } from "react-intl";

// Day/Night theme for the PUBLIC respondent surface only (interview, intro,
// demographics, review, done). The admin app is untouched — these are plain token
// objects the respondent components read, not a MUI theme override.
//
// Initial mode: a persisted manual choice wins; otherwise the OS preference.
export const THEME_KEY = "prompta.interview.theme";

const DAY = {
    mode: "day",
    appBg: "#f4f7fc",
    // Two large soft glows (radial-gradients are inherently blurred).
    glow1: "rgba(46,143,255,.14)",
    glow2: "rgba(16,185,170,.14)",
    question: "#00438e",
    say: "#6b7789",
    kicker: "#6b7789",
    progressTrack: "rgba(14,156,148,.16)",
    progressFill: "#0e9c94",
    pct: "#0e9c94",
    teal: "#0e9c94",
    cueBg: "rgba(14,156,148,.12)",
    cueText: "#0e9c94",
    pauseBorder: "rgba(0,67,142,.35)",
    pauseBg: "#ffffff",
    pauseText: "#00438e",
    submitBg: "#00438e",
    submitHover: "#003A75",
    submitText: "#ffffff",
    submitDisabledBg: "#e3e7ef",
    submitDisabledText: "#9aa4b4",
    orbInset: "#ffffff",
    orbGlow: "none",
    panelBg: "#ffffff",
    panelBorder: "rgba(0,67,142,.10)",
    panelDashed: "rgba(0,67,142,.22)",
    panelText: "#1a2b45",
    panelMuted: "#6b7789",
    topBarBg: "rgba(255,255,255,.86)",
    topBarBorder: "rgba(0,67,142,.08)",
    topBarShadow: "0 10px 30px -22px rgba(9,30,66,.55)",
    footerBg: "rgba(255,255,255,.86)",
    footerBorder: "rgba(0,67,142,.08)",
    cardShadow: "0 24px 60px -28px rgba(9,30,66,.35), 0 2px 8px rgba(9,30,66,.06)",
    ctaShadow: "0 10px 24px -8px rgba(0,67,142,.42)",
    chipShadow: "0 2px 10px rgba(16,42,80,.06)",
    progressGlow: "0 0 8px rgba(14,156,148,.45)",
    fieldBg: "#ffffff",
    toggleColor: "#00438e",
    heading: "#00438e",

    // ── Carded respondent forms (start demographics + post-interview Likert) ──
    // Frosted-glass cards on the ambient page. `formCardBgSolid` is the
    // @supports-not(backdrop-filter) fallback, so the card never reads as a
    // washed-out ghost on browsers without blur.
    formCardBg: "rgba(255,255,255,.72)",
    formCardBgSolid: "rgba(255,255,255,.9)",
    formCardBlur: "blur(10px)",
    formCardBorder: "rgba(255,255,255,.65)",
    formCardShadow:
        "0 10px 26px rgba(16,32,64,.09), 0 2px 5px rgba(16,32,64,.05), inset 0 1px 0 rgba(255,255,255,.75)",
    // Numbered badge on each question card
    badgeBg: "rgba(0,67,142,.09)",
    badgeText: "#00438e",
    // Question label (Inter) vs. Likert statement (Lora serif)
    formLabel: "#1c2733",
    formSerif: "#00438e",
    formMuted: "#6b7789",
    // Choice rows + scale cells
    optBorder: "rgba(219,227,238,.9)",
    optBg: "rgba(255,255,255,.5)",
    optText: "#1c2733",
    optHoverBorder: "#2ea3ff",
    optHoverBg: "rgba(242,248,255,.85)",
    optSelBorder: "#00438e",
    optSelBg: "#eaf3ff",
    optSelText: "#1c2733",
    radioBorder: "#c4cedd",
    radioFill: "#00438e",
    scaleNum: "#00438e",
    scaleLabel: "#6b7789",
    scaleSelBorder: "#00438e",
    scaleSelBg: "#00438e",
    scaleSelText: "#ffffff",
    // The one primary pill on each form
    formBtnBg: "#00438e",
    formBtnHover: "#003A75",
    formBtnShadow: "0 8px 20px rgba(0,67,142,.25)",
    // Unanswered-required ring + helper
    danger: "#e24b4a",
    focusRing: "rgba(46,143,255,.35)",
    // The 56px header orb's outer glow (dark only)
    headerOrbGlow: "none",

    // The OUTER elevated sheet the main content floats in. Page-level chrome (the
    // top bar and the day/night switch) deliberately sits OUTSIDE it.
    sheetBg: "rgba(255,255,255,.62)",
    sheetBgSolid: "rgba(255,255,255,.9)",
    sheetBlur: "blur(16px)",
    sheetBorder: "rgba(255,255,255,.7)",
    sheetShadow:
        "0 26px 60px rgba(16,32,64,.20), 0 6px 18px rgba(16,32,64,.08), inset 0 1px 0 rgba(255,255,255,.8)",
};

const NIGHT = {
    mode: "night",
    appBg: "#0a1728",
    glow1: "rgba(46,143,255,.30)",
    glow2: "rgba(16,185,170,.30)",
    question: "#eef4fd",
    say: "#9fb2cd",
    kicker: "#6f88ab",
    progressTrack: "rgba(255,255,255,.14)",
    progressFill: "#3ad2c6",
    pct: "#3ad2c6",
    teal: "#3ad2c6",
    cueBg: "rgba(58,210,198,.15)",
    cueText: "#3ad2c6",
    pauseBorder: "rgba(255,255,255,.22)",
    pauseBg: "rgba(255,255,255,.07)",
    pauseText: "#dbe7f6",
    submitBg: "#2e6fce",
    submitHover: "#2a63b8",
    submitText: "#ffffff",
    submitDisabledBg: "rgba(255,255,255,.12)",
    submitDisabledText: "#8ba0bd",
    orbInset: "#0c2340",
    orbGlow: "0 0 42px rgba(46,143,255,.35)",
    panelBg: "#12263f",
    panelBorder: "rgba(255,255,255,.10)",
    panelDashed: "rgba(255,255,255,.20)",
    panelText: "#eef4fd",
    panelMuted: "#9fb2cd",
    topBarBg: "rgba(10,23,40,.72)",
    topBarBorder: "rgba(255,255,255,.08)",
    topBarShadow: "0 14px 34px -24px rgba(0,0,0,.9)",
    footerBg: "rgba(10,23,40,.80)",
    footerBorder: "rgba(255,255,255,.08)",
    cardShadow: "0 30px 70px -30px rgba(0,0,0,.7), 0 2px 10px rgba(0,0,0,.4)",
    ctaShadow: "0 12px 28px -10px rgba(46,111,206,.6)",
    chipShadow: "0 2px 12px rgba(0,0,0,.4)",
    progressGlow: "0 0 10px rgba(58,210,198,.6)",
    fieldBg: "rgba(255,255,255,.05)",
    toggleColor: "#dbe7f6",
    heading: "#eef4fd",

    // ── Carded respondent forms ──
    // No blur on dark: a solid raised panel reads better against the midnight page.
    formCardBg: "#122238",
    formCardBgSolid: "#122238",
    formCardBlur: "none",
    formCardBorder: "rgba(255,255,255,.08)",
    formCardShadow: "0 14px 32px rgba(0,0,0,.42), inset 0 1px 0 rgba(255,255,255,.06)",
    badgeBg: "rgba(58,210,198,.16)",
    badgeText: "#7fe3d4",
    formLabel: "#eaf1fb",
    formSerif: "#eef4fd",
    formMuted: "#9fb2cd",
    optBorder: "rgba(255,255,255,.14)",
    optBg: "rgba(255,255,255,.04)",
    optText: "#dbe7f6",
    optHoverBorder: "#2ea3ff",
    optHoverBg: "rgba(46,143,255,.10)",
    // Selected switches to the teal accent on dark
    optSelBorder: "#3ad2c6",
    optSelBg: "rgba(58,210,198,.14)",
    optSelText: "#eaf6f4",
    radioBorder: "rgba(255,255,255,.32)",
    radioFill: "#3ad2c6",
    scaleNum: "#9cc2ff",
    scaleLabel: "#8ba0bd",
    scaleSelBorder: "#3ad2c6",
    scaleSelBg: "rgba(58,210,198,.20)",
    scaleSelText: "#eaf6f4",
    formBtnBg: "#2b6fd6",
    formBtnHover: "#2560bb",
    formBtnShadow: "0 8px 22px rgba(43,111,214,.4)",
    danger: "#f09595",
    focusRing: "rgba(58,210,198,.4)",
    headerOrbGlow: "0 0 34px rgba(46,143,255,.5)",

    // Outer sheet — solid on dark, no blur.
    sheetBg: "#0f1e33",
    sheetBgSolid: "#0f1e33",
    sheetBlur: "none",
    sheetBorder: "rgba(255,255,255,.07)",
    sheetShadow: "0 28px 64px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.05)",
};

const TOKENS = { day: DAY, night: NIGHT };

// A remembered manual choice wins; otherwise default to LIGHT (day). We
// deliberately do NOT follow the OS prefers-color-scheme — the interview opens in
// light unless the respondent has toggled to night before.
const readInitial = () => {
    try {
        const saved = window.localStorage.getItem(THEME_KEY);
        if (saved === "day" || saved === "night") return saved;
    } catch (e) {
        /* no-op */
    }
    return "day";
};

// The day tokens, for a theme built on top of them (the Modern survey runner's).
export const DAY_TOKENS = DAY;

// The two soft ambient glows as a background layer for the root surface.
export const glowBackground = (t) =>
    `radial-gradient(55rem 55rem at 0% 0%, ${t.glow1}, transparent 62%), ` +
    `radial-gradient(55rem 55rem at 100% 100%, ${t.glow2}, transparent 62%)`;

// A screen's own background: the ambient glow, or nothing when the theme carries
// a `pageBackground` that the page around the screen already paints.
export const pageBackgroundSx = (t) =>
    t.pageBackground ? { backgroundColor: "transparent" } : { backgroundColor: t.appBg, backgroundImage: glowBackground(t) };

// sx for MUI TextFields so they read on the current theme's panel surface.
export const themedFieldSx = (t) => ({
    "& .MuiOutlinedInput-root": {
        backgroundColor: t.fieldBg,
        color: t.panelText,
        "& fieldset": { borderColor: t.panelBorder },
        "&:hover fieldset": { borderColor: t.teal },
        "&.Mui-focused fieldset": { borderColor: t.teal },
    },
    "& .MuiInputBase-input::placeholder": { color: t.panelMuted, opacity: 1 },
});

export function useInterviewTheme() {
    const [mode, setMode] = useState(readInitial);

    const toggle = useCallback(() => {
        setMode((m) => {
            const next = m === "night" ? "day" : "night";
            try {
                window.localStorage.setItem(THEME_KEY, next);
            } catch (e) {
                /* no-op */
            }
            return next;
        });
    }, []);

    const tokens = useMemo(() => TOKENS[mode] || DAY, [mode]);
    return { mode, tokens, toggle };
}

// Day/Night switch — a sliding track whose knob carries the active sun/moon, with
// faint stars fading in on the night side. role="switch" + keyboard + focus ring.
export function ThemeSwitch({ mode, onToggle, sx }) {
    const intl = useIntl();
    if (!onToggle) return null; // a fixed look (the Modern runner's) has no switch
    const night = mode === "night";
    const label = night
        ? intl.formatMessage({ id: "conversation.theme_day", defaultMessage: "Switch to day mode" })
        : intl.formatMessage({ id: "conversation.theme_night", defaultMessage: "Switch to night mode" });
    return (
        <Tooltip title={label}>
            <Box
                component="button"
                type="button"
                role="switch"
                aria-checked={night}
                aria-label={label}
                onClick={onToggle}
                sx={{
                    position: "relative",
                    flexShrink: 0,
                    width: 60,
                    height: 30,
                    p: 0,
                    m: 0,
                    borderRadius: "999px",
                    cursor: "pointer",
                    border: night ? "1px solid rgba(255,255,255,.14)" : "1px solid rgba(0,67,142,.14)",
                    background: night
                        ? "linear-gradient(135deg, #16294a 0%, #0d1e35 100%)"
                        : "linear-gradient(135deg, #cfe6ff 0%, #eef5ff 100%)",
                    boxShadow: night ? "inset 0 1px 3px rgba(0,0,0,.5)" : "inset 0 1px 3px rgba(0,67,142,.12)",
                    transition: "background .35s ease, border-color .35s ease, box-shadow .35s ease",
                    outline: "none",
                    "&:focus-visible": {
                        boxShadow: night ? "0 0 0 3px rgba(58,210,198,.5)" : "0 0 0 3px rgba(14,156,148,.4)",
                    },
                    ...sx,
                }}
            >
                {/* Faint stars — night only */}
                <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, opacity: night ? 1 : 0, transition: "opacity .4s ease" }}>
                    <Box sx={{ position: "absolute", top: 8, left: 12, width: 2, height: 2, borderRadius: "50%", backgroundColor: "rgba(255,255,255,.75)" }} />
                    <Box sx={{ position: "absolute", top: 17, left: 18, width: 1.5, height: 1.5, borderRadius: "50%", backgroundColor: "rgba(255,255,255,.5)" }} />
                    <Box sx={{ position: "absolute", top: 6, left: 23, width: 1.5, height: 1.5, borderRadius: "50%", backgroundColor: "rgba(255,255,255,.6)" }} />
                </Box>
                {/* Knob carrying the active icon */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        top: 3,
                        left: night ? 33 : 3,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: night ? "linear-gradient(180deg,#e9effb,#c4d3ee)" : "linear-gradient(180deg,#ffffff,#fff2d3)",
                        boxShadow: night ? "0 2px 6px rgba(0,0,0,.5)" : "0 2px 6px rgba(0,67,142,.28)",
                        transition: "left .3s cubic-bezier(.4,0,.2,1), background .35s ease",
                    }}
                >
                    {night ? <MoonIcon sx={{ fontSize: 15, color: "#2b4372" }} /> : <SunIcon sx={{ fontSize: 15, color: "#f0a417" }} />}
                </Box>
            </Box>
        </Tooltip>
    );
}
